/**
 * ComboSystem — real-time combo tracking with multiplier tiers.
 *
 * Tapping within 300ms of previous tap maintains/grows the combo.
 * Gap >800ms causes decay. Wrong key (same key twice) resets.
 * Pattern chain completion grants instant +20 combo.
 *
 * Tiers:
 *   1-9:     x1.0  White      Basic
 *   10-24:   x1.5  Green      Subtle glow
 *   25-49:   x2.0  Cyan       Screen pulse
 *   50-99:   x3.0  Magenta    Shake + particles
 *   100-199: x5.0  Gold       Heavy shake + burst
 *   200+:    x10.0 Rainbow    Full celebration
 */

const COMBO_TIERS = [
  { min: 200, multiplier: 10.0, color: 0xFFFFFF, name: 'RAINBOW',  shakeIntensity: 0.004 },
  { min: 100, multiplier: 5.0,  color: 0xFFD700, name: 'GOLD',     shakeIntensity: 0.003 },
  { min: 50,  multiplier: 3.0,  color: 0xFF00FF, name: 'MAGENTA',  shakeIntensity: 0.002 },
  { min: 25,  multiplier: 2.0,  color: 0x00FFFF, name: 'CYAN',     shakeIntensity: 0.001 },
  { min: 10,  multiplier: 1.5,  color: 0x39FF14, name: 'GREEN',    shakeIntensity: 0 },
  { min: 0,   multiplier: 1.0,  color: 0xFFFFFF, name: 'WHITE',    shakeIntensity: 0 },
];

// Milestones that trigger particle bursts
const COMBO_MILESTONES = [10, 25, 50, 100, 200, 500];

// Timing constants (ms)
const COMBO_WINDOW = 300;
const DECAY_START = 800;
const DECAY_RATE = 0.5; // lose 50% of combo per second once decaying

const ComboSystem = {
  /**
   * Create a fresh combo state object.
   * @returns {Object} Initial combo state
   */
  create() {
    return {
      combo: 0,
      maxCombo: 0,
      lastTapTime: 0,
      lastMilestone: 0,
      totalScore: 0,
    };
  },

  /**
   * Process a tap event. Returns a new state + event info.
   * @param {Object} state - Current combo state (not mutated)
   * @param {number} timestamp - Current time in ms
   * @param {boolean} correctKey - Whether the key alternation was correct
   * @returns {{ state: Object, event: Object }} New state and event info
   */
  onTap(state, timestamp, correctKey) {
    if (!correctKey) {
      // Wrong key = combo reset
      return {
        state: { ...state, combo: 0, lastTapTime: timestamp },
        event: { type: 'reset', combo: 0 },
      };
    }

    const elapsed = state.lastTapTime > 0 ? timestamp - state.lastTapTime : 0;
    let newCombo = state.combo;

    if (elapsed > 0 && elapsed <= COMBO_WINDOW) {
      // Fast tap — increment combo
      newCombo = state.combo + 1;
    } else if (elapsed > DECAY_START) {
      // Too slow — combo was decaying, this tap restarts from reduced value
      const decaySeconds = (elapsed - DECAY_START) / 1000;
      const decayed = Math.floor(state.combo * Math.pow(1 - DECAY_RATE, decaySeconds));
      newCombo = Math.max(0, decayed) + 1;
    } else {
      // Within acceptable range but not "fast" — maintain but don't grow
      newCombo = state.combo + 1;
    }

    const newMax = Math.max(state.maxCombo, newCombo);

    // Check for milestone
    let milestone = null;
    for (const m of COMBO_MILESTONES) {
      if (newCombo >= m && state.combo < m && m > state.lastMilestone) {
        milestone = m;
        break;
      }
    }

    const tier = ComboSystem.getTier(newCombo);
    const points = Math.round(10 * tier.multiplier);

    const newState = {
      combo: newCombo,
      maxCombo: newMax,
      lastTapTime: timestamp,
      lastMilestone: milestone || state.lastMilestone,
      totalScore: state.totalScore + points,
    };

    return {
      state: newState,
      event: {
        type: milestone ? 'milestone' : 'tap',
        combo: newCombo,
        milestone,
        points,
        tier,
      },
    };
  },

  /**
   * Apply decay over time (call each frame).
   * @param {Object} state - Current combo state
   * @param {number} timestamp - Current time in ms
   * @returns {Object} New state with decay applied
   */
  applyDecay(state, timestamp) {
    if (state.combo <= 0 || state.lastTapTime <= 0) return state;

    const elapsed = timestamp - state.lastTapTime;
    if (elapsed <= DECAY_START) return state;

    const decaySeconds = (elapsed - DECAY_START) / 1000;
    const decayed = Math.floor(state.combo * Math.pow(1 - DECAY_RATE, decaySeconds));
    const newCombo = Math.max(0, decayed);

    if (newCombo === state.combo) return state;

    return { ...state, combo: newCombo };
  },

  /**
   * Add combo from a pattern chain completion.
   * @param {Object} state - Current combo state
   * @param {number} bonus - Combo points to add (default 20)
   * @returns {Object} New state with bonus applied
   */
  addPatternBonus(state, bonus = 20) {
    const newCombo = state.combo + bonus;
    return {
      ...state,
      combo: newCombo,
      maxCombo: Math.max(state.maxCombo, newCombo),
    };
  },

  /**
   * Get the current tier info for a combo count.
   * @param {number} combo
   * @returns {{ min, multiplier, color, name, shakeIntensity }}
   */
  getTier(combo) {
    for (const tier of COMBO_TIERS) {
      if (combo >= tier.min) return tier;
    }
    return COMBO_TIERS[COMBO_TIERS.length - 1];
  },

  /**
   * Get display information for the HUD.
   * @param {Object} state - Current combo state
   * @returns {{ combo, multiplier, tier, color, colorHex, isActive }}
   */
  getDisplayInfo(state) {
    const tier = ComboSystem.getTier(state.combo);
    return {
      combo: state.combo,
      maxCombo: state.maxCombo,
      multiplier: tier.multiplier,
      tierName: tier.name,
      color: tier.color,
      colorHex: '#' + tier.color.toString(16).padStart(6, '0'),
      shakeIntensity: tier.shakeIntensity,
      isActive: state.combo >= 10,
    };
  },
};

export default ComboSystem;
export { COMBO_TIERS, COMBO_MILESTONES };
